/*
 * Copyright (c) 2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import {CompanyFormDropdownOption} from "pages/Front/Booking/components/CompanyForm/CompanyFormDropdown";
import {useTranslation} from "react-i18next";
import {Company} from "types/entities/Company";
import {CompanyContact} from "types/entities/CompanyContact";
import {CompanyActivationStatus} from "types/enums/CompanyActivationStatus";
import {CompanyBranchType} from "types/enums/CompanyBranchType";
import {useMemo} from "react";
import {TFunction} from "i18next";

/** Decodes common HTML entities in strings coming from the API (e.g. &amp; → &). */
const HTML_ENTITIES: Record<string, string> = {
    '&amp;': '&',
    '&lt;': '<',
    '&gt;': '>',
    '&quot;': '"',
    '&#039;': "'",
    '&apos;': "'",
}
export const decodeHtmlEntities = (str: string): string =>
    str.replace(/&(?:amp|lt|gt|quot|#039|apos);/g, (match) => HTML_ENTITIES[match] ?? match)

const CONTACT_GENDER_LABEL_KEYS: Record<string, string> = {
    male: 'Male',
    female: 'Female',
    other: 'Other',
}

const CONTACT_GENDER_VALUES = ['male', 'female', 'other'] as const

export const getContactGenderOptions = (t: TFunction) =>
    CONTACT_GENDER_VALUES.map((value) => ({
        value,
        label: t(CONTACT_GENDER_LABEL_KEYS[value]),
    }))

export const useContactGenderOptions = () => {
    const { t } = useTranslation()

    return useMemo(() => getContactGenderOptions(t), [t])
}

export const getContactGenderLabel = (
    gender: string | null | undefined,
    t: TFunction
): string => {
    if (!gender) {
        return '-'
    }

    const labelKey = CONTACT_GENDER_LABEL_KEYS[gender]
    return labelKey ? t(labelKey) : gender
}

export const useGetBusinessCategories = (): { businessCategories: CompanyFormDropdownOption[], findByBusinessCategoryId: (category_id:string) => CompanyFormDropdownOption } => {
    const { t } = useTranslation();
    const businessCategories: CompanyFormDropdownOption[] = [
		{ id: "IndustrieAndProduktion", name: t("Industrie & Produktion"), position: 1, category: "A" },
		{ id: "GewerbeAndBauwesen", name: t("Gewerbe & Bauwesen"), position: 2, category: "A" },
		{ id: "HandelAndDetailhandel", name: t("Handel & Detailhandel"), position: 3, category: "A" },
		{ id: "FinanzwesenAndVersicherungen", name: t("Finanzwesen & Versicherungen"), position: 4, category: "A" },
		{ id: "GutertransportAndLogistik", name: t("Gütertransport & Logistik"), position: 5, category: "A" },
		{ id: "PharmaAndMedtech", name: t("Pharma & Medtech") , position: 6, category: "A" },
		{ id: "GastronomieTourismusUndHotellerie", name: t("Gastronomie, Tourismus & Hospitality"), position: 7, category: "A" },
		{ id: "AutomobilUndMobilitat", name: t("Automobilität"), position: 8, category: "A" },
		{ id: "PrivateEducationAndUniversitaten", name: t("Private Education & Universitäten"), position: 9, category: "A" },
		{ id: "TechnologieAndIT", name: t("Technologie & IT"), position: 10, category: "A" },
		{ id: "Immobilienbranche", name: t("Immobilienbranche"), position: 11, category: "A" },
		{ id: "GesundheitAndPflege", name: t("Gesundheit & Pflege"), position: 12, category: "B" },
		{ id: "OffentlicherSektor", name: t("Öffentlicher Sektor"), position: 13, category: "B" },
		{ id: "Dienstleistungen", name: t("Dienstleistungen"), position: 14, category: "B" },
		{ id: "Telekomunikation", name: t("Telekommunikation & Medien"), position: 15, category: "B" },
		{ id: "PersonenmobilitatAndVerkehr", name: t("Personenmobilität & Verkehr"), position: 16, category: "B" },
		{ id: "SchulenBildungAndSoziales", name: t("Schulen, öffentliche Bildung"), position: 18, category: "C" },
		{ id: "PrivateGeburtstagePolterabende", name: t("Private, Geburtstage & Polterabende"), position: 19, category: "C" },
		{ id: "SportAndVereine", name: t("Sport, Vereine & Soziales"), position: 20, category: "C" },
		{ id: "VerbandeAndOrganisationen", name: t("Verbände & Organisationen"), position: 21, category: "C" },
	];

	/** Dissolved categories remapped for display (existing records until BE migrates them). */
	const LEGACY_BUSINESS_CATEGORY_ALIASES: Record<string, string> = {
		FreizeitAndUnterhaltung: 'GastronomieTourismusUndHotellerie',
	}

	const findByBusinessCategoryId = (category_id: string) => {
		const resolvedId = LEGACY_BUSINESS_CATEGORY_ALIASES[category_id] ?? category_id
		return businessCategories.find(businessCategory => businessCategory.id === resolvedId);
	}
    return {
        businessCategories,
		findByBusinessCategoryId
    }
}

export const employeeSizeOptions: CompanyFormDropdownOption[] = [
    { id: "1-9", name: "1-9" },
    { id: "10-19", name: "10-19" },
    { id: "20-50", name: "20-50" },
    { id: "51-100", name: "51-100" },
    { id: "101-250", name: "101-250" },
    { id: "251-500", name: "251-500" },
    { id: "500+", name: "500+" },
];

export interface CompanyActivationStatusDescriptor {
    value: CompanyActivationStatus
    label: string
}

export const useCompanyActivationStatusOptions = () => {
    const { t } = useTranslation()

    const options = useMemo<Array<{ label: string; value: CompanyActivationStatus }>>(() => [
        { label: t('Active'), value: CompanyActivationStatus.Active },
        { label: t('Archived'), value: CompanyActivationStatus.Archived },
        { label: t('Disabled'), value: CompanyActivationStatus.Disabled },
    ], [t])

    const map = useMemo(() => {
        const statusMap = {} as Record<CompanyActivationStatus, CompanyActivationStatusDescriptor>
        options.forEach(option => { statusMap[option.value] = option })
        return statusMap
    }, [options])

    const getStatusDescriptor = (value: CompanyActivationStatus) => map[value] ?? map[CompanyActivationStatus.Active]

    return { options, map, getStatusDescriptor }
}

export interface CompanyBranchTypeDescriptor {
    value: CompanyBranchType
    label: string
}

/** Legacy aliases from older backend values that map to canonical enum values. */
const BRANCH_TYPE_ALIASES: Partial<Record<string, CompanyBranchType>> = {
    account: CompanyBranchType.Company,
}

const BRANCH_TYPE_VALUES = new Set<string>(Object.values(CompanyBranchType))

export const normalizeCompanyBranchType = (
    value?: string | CompanyBranchType | null
): CompanyBranchType => {
    if (!value) return CompanyBranchType.Company
    const normalized = String(value).toLowerCase()
    if (BRANCH_TYPE_ALIASES[normalized]) return BRANCH_TYPE_ALIASES[normalized]!
    if (BRANCH_TYPE_VALUES.has(normalized)) return normalized as CompanyBranchType
    return CompanyBranchType.Company
}

const NON_BUSINESS_ACCOUNT_TYPES = new Set<CompanyBranchType>([
    CompanyBranchType.School,
    CompanyBranchType.Verein,
    CompanyBranchType.Private,
])

export const isNonBusinessAccountType = (type: CompanyBranchType): boolean =>
    NON_BUSINESS_ACCOUNT_TYPES.has(type)

export const AUTO_BUSINESS_CATEGORY_BY_BRANCH_TYPE: Partial<Record<CompanyBranchType, string>> = {
    [CompanyBranchType.School]: 'SchulenBildungAndSoziales',
    [CompanyBranchType.Verein]: 'SportAndVereine',
    [CompanyBranchType.Private]: 'PrivateGeburtstagePolterabende',
}

export const getAutoBusinessCategoryForBranchType = (
    branchType?: CompanyBranchType | string | null
): string | undefined => {
    if (!branchType) {
        return undefined
    }

    return AUTO_BUSINESS_CATEGORY_BY_BRANCH_TYPE[branchType as CompanyBranchType]
}

export const useCompanyBranchTypeOptions = () => {
    const options = useMemo<Array<{ label: string; value: CompanyBranchType }>>(() => [
        { label: 'Firma', value: CompanyBranchType.Company },
        { label: 'Privat', value: CompanyBranchType.Private },
        { label: 'Schule', value: CompanyBranchType.School },
        { label: 'Verein', value: CompanyBranchType.Verein },
        { label: 'Branch', value: CompanyBranchType.Branch },
        { label: 'Muttergesellschaft', value: CompanyBranchType.Parent },
    ], [])

    const map = useMemo(() => {
        const typeMap = {} as Record<CompanyBranchType, CompanyBranchTypeDescriptor>
        options.forEach(option => { typeMap[option.value] = option })
        return typeMap
    }, [options])

    const getTypeDescriptor = (value: CompanyBranchType) => map[value] ?? map[CompanyBranchType.Parent]

    return { options, map, getTypeDescriptor }
}

export enum CreateCompanyStep {
    Basic = 1,
    Contact = 2,
    Address = 3,
}

export interface StepConfig {
    id: CreateCompanyStep
    labelKey: string
}

export const COMPANY_STEPS_CONFIG: StepConfig[] = [
    { id: CreateCompanyStep.Basic, labelKey: 'Basic / Legal / Notes' },
    { id: CreateCompanyStep.Contact, labelKey: 'Contact' },
    { id: CreateCompanyStep.Address, labelKey: 'Address' },
]

export const getCreateCompanyStepLabel = (step: CreateCompanyStep, t: (key: string) => string): string => {
    const config = COMPANY_STEPS_CONFIG.find(s => s.id === step)
    return config ? t(config.labelKey) : ''
}

export const buildCreateCompanySteps = (t: (key: string) => string) => {
    return COMPANY_STEPS_CONFIG.map(config => ({
        id: config.id,
        label: t(config.labelKey),
    }))
}

export const pruneEmpty = (
    obj: Record<string, unknown>,
    preserveKeys: string[] = []
) =>
    Object.fromEntries(
        Object.entries(obj).filter(
            ([k, v]) =>
                preserveKeys.includes(k) ||
                (v !== undefined &&
                    v !== null &&
                    !(typeof v === 'string' && v.trim() === ''))
        )
    )

const isEmptyPersistableValue = (value: unknown): boolean =>
    value === undefined ||
    value === null ||
    (typeof value === 'string' && value.trim() === '')

const isNonEmptyPersistableValue = (value: unknown): boolean =>
    value !== undefined &&
    value !== null &&
    (typeof value !== 'string' || value.trim() !== '')

/** Keeps submitted contact fields when API response omits them after save. */
export const mergeContactAfterSave = (
    submitted: Partial<CompanyContact>,
    response: CompanyContact
): CompanyContact => {
    const merged: CompanyContact = {...response}

    const preserveIfMissing = (
        key: keyof Pick<
            CompanyContact,
            'position' | 'origin' | 'title' | 'custom_title' | 'gender' | 'note'
        >
    ) => {
        const responseValue = merged[key]
        const submittedValue = submitted[key]

        if (isEmptyPersistableValue(responseValue) && isNonEmptyPersistableValue(submittedValue)) {
            Object.assign(merged, {[key]: submittedValue})
        }
    }

    preserveIfMissing('position')
    preserveIfMissing('origin')
    preserveIfMissing('title')
    preserveIfMissing('custom_title')
    preserveIfMissing('gender')
    preserveIfMissing('note')

    if (
        (merged.tags === undefined || merged.tags === null) &&
        Array.isArray(submitted.tags)
    ) {
        merged.tags = submitted.tags
    }

    if (
        (merged.is_generic === undefined || merged.is_generic === null) &&
        typeof submitted.is_generic === 'boolean'
    ) {
        merged.is_generic = submitted.is_generic
    }

    return merged
}

/** Keeps submitted company fields when API response omits them after save. */
export const mergeCompanyAfterSave = (
    submitted: Partial<Company>,
    response: Company
): Company => {
    const merged: Company = {...response}

    const preferSubmitted = (
        key: keyof Pick<Company, 'origin' | 'business_category' | 'type' | 'employee_count'>
    ) => {
        if (!(key in submitted)) {
            return
        }

        const submittedValue = submitted[key]
        const responseValue = merged[key]

        // Trust what we sent when API omits the field or echoes a stale value.
        if (isNonEmptyPersistableValue(submittedValue)) {
            Object.assign(merged, {[key]: submittedValue})
            return
        }

        if (isEmptyPersistableValue(responseValue) && submittedValue === null) {
            Object.assign(merged, {[key]: null})
        }
    }

    preferSubmitted('origin')
    preferSubmitted('business_category')
    preferSubmitted('type')
    preferSubmitted('employee_count')

    if (
        (merged.tags === undefined || merged.tags === null) &&
        Array.isArray(submitted.tags)
    ) {
        merged.tags = submitted.tags
    }

    return merged
}

const buildPersonName = (firstName?: string | null, lastName?: string | null): string =>
    [firstName, lastName]
        .map((value) => value?.trim())
        .filter((value): value is string => Boolean(value))
        .join(' ')

export const getCompanyDisplayName = (company?: Partial<Company> | null): string => {
    if (!company) {
        return ''
    }

    const companyName = company.name?.trim() || ''
    if (company.type !== CompanyBranchType.Private) {
        return decodeHtmlEntities(companyName)
    }

    const defaultContact = company.contacts?.find(
        (contact) => contact.id === company.default_contact_id
    )
    const fallbackContact = defaultContact ?? company.contacts?.[0]
    const contactName = buildPersonName(
        fallbackContact?.first_name,
        fallbackContact?.last_name
    )

    if (contactName) {
        return contactName
    }

    const privateName = buildPersonName(company.first_name, company.last_name)
    if (privateName) {
        return privateName
    }

    // For private accounts created without contact/person data, use account name.
    return decodeHtmlEntities(companyName)
}